import { FormControl, TextField, Button } from '@mui/material'
import React, { useEffect, useState, useMemo } from 'react'
import TPCheckbox from "../../TemplateProperties/TPCheckbox";
import _, { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';
import { MoreHorizOutlined } from '@mui/icons-material';
import WorkflowSessionModal from '../../ItemProperties/WorkflowSessionModal';
import { decryptString, encryptString, stringifyJSONAttributes, stringToBooleanConversion } from '../../../utils/utilFunctions';

const Azure = ({ 
    connectionDetails, 
    handleBodyContent, 
    direction,
    repoPDC = {}, 
    useConnection, 
    disableControl = false, 
    screen = '', 
}) => {
  const { t } = useTranslation();
  const [accountName, setAccountName] = useState('');
  const [accountKey, setAccountKey] = useState('');
  const [fileSystem, setFileSystem] = useState('');
  const [createFileSystem, setCreateFileSystem] = useState(true);
  const [directory, setDirectory] = useState('');
  const [createDirectory, setCreateDirectory] = useState(true);
  const [filename, setFilename] = useState('');
  const [deleteFiles, setDeleteFiles] = useState(false);
  const [appendFile, setAppendFile] = useState(false);
  const [content, setContent] = useState(null);
  const [valueLabel, setValueLabel] = useState("");
  const [workflowSessionModal, setWorkflowSessionModal] = useState({
      open: false,
      isFirstTime: false,
  });

  const workflowMappings = {
      [t("ACCOUNT_NAME")]: {
          stateSetter: setAccountName,
          key: "AZURE_ACCOUNT_NAME",
          value: accountName,
      },
      [t("ACCOUNT_KEY_TITLE")]: {
          stateSetter: setAccountKey,
          key: "AZURE_ACCOUNT_KEY",
          value: decryptString(accountKey || "", process.env.REACT_APP_SECRET_KEY2),
      },
      [t("LBL_AZURE_FILESYSTEM")]: {
          stateSetter: setFileSystem,
          key: "AZURE_FILESYSTEM",
          value: fileSystem,
      },
      [t("DIRECTORY_")]: {
          stateSetter: setDirectory,
          key: "AZURE_DIRECTORY",
          value: directory,
      },
      [t("LBL_AZURE_FILENAME")]: {
          stateSetter: setFilename,
          key: "AZURE_FILENAME",
          value: filename,
      },
  };

  const valuesHandler = (value) => {
      if (workflowMappings[valueLabel]) {
          const { stateSetter, key } = workflowMappings[valueLabel];
          stateSetter(value);
          onInputChange(key, key === "AZURE_ACCOUNT_KEY" ? encryptString(value, process.env.REACT_APP_SECRET_KEY2) : value);
      }
  };

  const getFontWeight = (control, currentValue, useConnection) => {
      if (!repoPDC) return "normal";
      return repoPDC?.[control] !== currentValue &&
          useConnection === "preDefined"
          ? "bold"
          : "normal";
  };

  const fontWeights = useMemo(() => {
      return {
          accountName: getFontWeight("AZURE_ACCOUNT_NAME", accountName, useConnection),
          accountKey: getFontWeight("AZURE_ACCOUNT_KEY", accountKey, useConnection),
          fileSystem: getFontWeight("AZURE_FILESYSTEM", fileSystem, useConnection),
          directory: getFontWeight("AZURE_DIRECTORY", directory, useConnection),
          filename: getFontWeight("AZURE_FILENAME", filename, useConnection),
      };
  }, [accountName, accountKey, fileSystem, directory, filename, useConnection, repoPDC]);

  const debouncedContentUpdate = debounce(handleBodyContent, 500);

  const onInputChange = (name, value) => {
    if(['GET_DATA', 'SEND_DATA'].includes(screen)){
        setContent((prev) => ({ add:{ ...prev?.add,  [name]: value} }));
        return;
    }
    setContent((prev) => {
      return { ...prev, [name]: value };
    });
  }

  useEffect(() => {
    if(!_.isEqual(content, connectionDetails)) debouncedContentUpdate(content);
  }, [content]);

  useEffect(() => {
    setAccountName(connectionDetails?.AZURE_ACCOUNT_NAME || '');
    setAccountKey(decryptString(connectionDetails?.AZURE_ACCOUNT_KEY || "", process.env.REACT_APP_SECRET_KEY2) || '');
    setFileSystem(connectionDetails?.AZURE_FILESYSTEM || '');
    if (connectionDetails?.CREATE_FILESYS) setCreateFileSystem(stringToBooleanConversion(connectionDetails?.CREATE_FILESYS));
    else setCreateFileSystem(true);
    setDirectory(connectionDetails?.AZURE_DIRECTORY || '');
    if (connectionDetails?.CREATE_DIRS) setCreateDirectory(stringToBooleanConversion(connectionDetails?.CREATE_DIRS));
    else setCreateDirectory(true);
    setFilename(connectionDetails?.AZURE_FILENAME || '');
    if (connectionDetails?.DELETE) {
      if (stringToBooleanConversion(connectionDetails?.DELETE)) setDeleteFiles(false);
      else setDeleteFiles(true);
    } else setDeleteFiles(false)
    setAppendFile(stringToBooleanConversion(connectionDetails?.APPEND_FILE));
    setContent({ ...connectionDetails });
  }, [connectionDetails])

  return (
    <>
      <div style={{ display: "flex", gap: "4px" }}>
        <FormControl className="width-100" size="small">
          <TextField
            variant="outlined"
            size="small"
            label={t("ACCOUNT_NAME")}
            type="text"
            value={accountName}
            disabled={disableControl || false}
            inputProps={{
                style: { fontWeight: fontWeights.accountName }
            }}
            onChange={(e) => {
              onInputChange(e.target.name, e.target.value);
              setAccountName(e.target.value);
            }}
            className={"width-100"}
            name={"AZURE_ACCOUNT_NAME"}
            autoComplete="off"
          />
        </FormControl>
        {["GET_DATA", "SEND_DATA"].includes(screen) && (
            <WorkflowSessionModalButton
                disableControl={disableControl || false}
                label={t("ACCOUNT_NAME")}
                setWorkflowSessionModal={setWorkflowSessionModal}
                setValueLabel={setValueLabel}
            />
        )}
      </div>
    <div style={{ display: "flex", gap: "4px" }}>
        <FormControl className="width-100" size="small">
          <TextField
            variant="outlined"
            size="small"
            label={t("ACCOUNT_KEY_TITLE")}
            type="text"
            value={accountKey}
            disabled={disableControl || false}
            InputLabelProps={{ shrink: !!accountKey }}
            inputProps={{
                style: { fontWeight: fontWeights.accountKey }
            }}
            onChange={(e) => {
              onInputChange(e.target.name, encryptString(e.target.value, process.env.REACT_APP_SECRET_KEY2));
              setAccountKey(e.target.value);
            }}
            className={"width-100"}
            name={"AZURE_ACCOUNT_KEY"}
            autoComplete="off"
          />
        </FormControl>
        {["GET_DATA", "SEND_DATA"].includes(screen) && (
            <WorkflowSessionModalButton
                disableControl={disableControl || false}
                label={t("ACCOUNT_KEY_TITLE")}
                setWorkflowSessionModal={setWorkflowSessionModal}
                setValueLabel={setValueLabel}
            />
        )}
      </div>
      <div style={{ display: "flex", gap: "4px" }}>
        <FormControl className="width-100" size="small">
          <TextField
            variant="outlined"
            size="small"
            label={t("LBL_AZURE_FILESYSTEM")}
            type="text"
            value={fileSystem}
            disabled={disableControl || false}
            inputProps={{
                style: { fontWeight: fontWeights.fileSystem }
            }}
            onChange={(e) => {
              onInputChange(e.target.name, e.target.value);
              setFileSystem(e.target.value)
            }}
            className={"width-100"}
            name={"AZURE_FILESYSTEM"}
            autoComplete="off"
          />
        </FormControl>
        {["GET_DATA", "SEND_DATA"].includes(screen) && (
            <WorkflowSessionModalButton
                disableControl={disableControl || false}
                label={t("LBL_AZURE_FILESYSTEM")}
                setWorkflowSessionModal={setWorkflowSessionModal}
                setValueLabel={setValueLabel}
            />
        )}
      </div>
      {direction === 'OUT' && <TPCheckbox
        label={t("BTN_CREATE_FILE_SYSTEM")}
        checked={createFileSystem}
        disabled={disableControl || false}
        handleCheck={(checked) => {
          onInputChange('CREATE_FILESYS', stringifyJSONAttributes(checked));
          setCreateFileSystem(checked);
        }}
      />}
      <div style={{ display: "flex", gap: "4px" }}>
        <FormControl className="width-100" size="small">
          <TextField
            variant="outlined"
            size="small"
            label={t("DIRECTORY_")}
            type="text"
            value={directory}
            disabled={disableControl || false}
            inputProps={{
                style: { fontWeight: fontWeights.directory }
            }}
            onChange={(e) => {
              onInputChange(e.target.name, e.target.value);
              setDirectory(e.target.value)
            }}
            className={"width-100"}
            name={"AZURE_DIRECTORY"}
            autoComplete="off"
          />
        </FormControl>
        {["GET_DATA", "SEND_DATA"].includes(screen) && (
            <WorkflowSessionModalButton
                disableControl={disableControl || false}
                label={t("DIRECTORY_")}
                setWorkflowSessionModal={setWorkflowSessionModal}
                setValueLabel={setValueLabel}
            />
        )}
      </div>
      {direction === 'OUT' && <TPCheckbox
        label={t("BTN_CREATE_FILE_DIRECTORY")}
        checked={createDirectory}
        disabled={disableControl || false}
        handleCheck={(checked) => {
          onInputChange('CREATE_DIRS', stringifyJSONAttributes(checked));
          setCreateDirectory(checked);
        }}
      />}
      <div style={{ display: "flex", gap: "4px" }}>
        <FormControl className="width-100" size="small">
          <TextField
            variant="outlined"
            size="small"
            label={t("LBL_AZURE_FILENAME")}
            type="text"
            value={filename}
            disabled={disableControl || false}
            inputProps={{
                style: { fontWeight: fontWeights.filename }
            }}
            onChange={(e) => {
              onInputChange(e.target.name, e.target.value);
              setFilename(e.target.value);
            }}
            className={"width-100"}
            name={"AZURE_FILENAME"}
            autoComplete="off"
          />
        </FormControl>
        {["GET_DATA", "SEND_DATA"].includes(screen) && (
            <WorkflowSessionModalButton
                disableControl={disableControl || false}
                label={t("LBL_AZURE_FILENAME")}
                setWorkflowSessionModal={setWorkflowSessionModal}
                setValueLabel={setValueLabel}
            />
        )}
      </div>
      {direction === 'IN' && <TPCheckbox
        label={t("BTN_DO_NOT_DELETE")}
        checked={deleteFiles}
        disabled={disableControl || false}
        handleCheck={(checked) => {
          onInputChange('DELETE', stringifyJSONAttributes(!checked));
          setDeleteFiles(checked);
        }}
      />}
      {direction === 'OUT' && <TPCheckbox
        label={t("APPEND_FILE")}
        checked={appendFile}
        disabled={disableControl || false}
        handleCheck={(checked) => {
          onInputChange('APPEND_FILE', stringifyJSONAttributes(checked));
          setAppendFile(checked);
        }}
      />}

      <WorkflowSessionModal
          valueLabel={valueLabel}
          showAccountData={true}
          showCallingWorkflow={true}
          showSystemData={true}
          showWorkflowSession={true}
          showDateAndTime={true}
          openModal={workflowSessionModal}
          setModalOpen={setWorkflowSessionModal}
          handleClose={() =>
              setWorkflowSessionModal({ open: false, isFirstTime: false })
          }
          initialValue={workflowMappings[valueLabel]?.value}
          inputType="text"
          valuesHandler={(value) => {
              valuesHandler(value);
          }}
      />
    </>
  )
}

export default Azure

const WorkflowSessionModalButton = ({ setWorkflowSessionModal, label, disableControl, setValueLabel }) => {
    return (
        <Button
            size="small"
            disabled={disableControl || false}
            variant="outlined"
            onClick={() => {
                setValueLabel(label);
                setWorkflowSessionModal({ open: true, isFirstTime: true });
            }}
            sx={{ width: "40px", minWidth: "40px" }}>
            <MoreHorizOutlined fontSize="small" />
        </Button>
    );
};